import {
    LucideIconData,
    LayoutDashboard,
    ShoppingCart,
    Monitor,
    Package,
    Truck,
    Warehouse,
    Users,
    BarChart3,
    Settings,
    MapPin,
    Shield,
    Receipt,
    Globe,
    CreditCard,
    Bell,
    ShoppingBag,
    DollarSign,
    Layers,
    Tag,
    Gift,
    ClipboardList,
    List,
    ListTree,
    Plus,
    ArrowLeftRight,
    TrendingUp,
    Activity,
    UserCheck,
    Store,
    Search,
    Wrench,
    BookOpen,
} from 'lucide-angular';
import { COLLECTIONS_FLAG } from '../../../features/collections/config/collection-routes.config';

export interface NavItem {
    /** Unique identifier for this nav item */
    id: string;
    /** Display label */
    label: string;
    /** Router path (absolute) */
    route: string;
    /** Lucide icon data */
    icon: LucideIconData;
    /** Sub-items shown in a dropdown */
    children?: NavChild[];
}

export interface NavChild {
    /** Unique identifier */
    id: string;
    /** Display label */
    label: string;
    /** Router path (absolute) */
    route: string;
    /** Lucide icon data */
    icon: LucideIconData;
    /** Feature flag that must be on for this entry to show. */
    flag?: string;
}

/** Drops child entries whose feature flag is off. */
export function withEnabledChildren(items: readonly NavItem[], isEnabled: (flag: string) => boolean): NavItem[] {
    return items.map(item => item.children
        ? { ...item, children: item.children.filter(child => !child.flag || isEnabled(child.flag)) }
        : item);
}

export const OPERATIONS_NAV_ITEMS: NavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        route: '/dashboard',
        icon: LayoutDashboard,
    },
    {
        id: 'orders',
        label: 'Orders',
        route: '/orders',
        icon: ShoppingCart,
    },
    {
        id: 'pos',
        label: 'POS',
        route: '/pos',
        icon: Monitor,
        // Registers / Cash Management are hidden: POS is a single top-level link.
        // children: [
        //     { id: 'pos-registers', label: 'Registers', route: '/pos/registers', icon: Monitor },
        //     { id: 'pos-cash', label: 'Cash Management', route: '/pos/cash-management', icon: DollarSign },
        // ],
    },
    {
        id: 'catalog',
        label: 'Catalog',
        route: '/catalog/products',
        icon: Package,
        children: [
            { id: 'catalog-products', label: 'Products', route: '/catalog/products', icon: Package },
            { id: 'catalog-categories', label: 'Categories', route: '/catalog/categories', icon: Layers },
            { id: 'catalog-collections', label: 'Collections', route: '/catalog/collections', icon: List, flag: COLLECTIONS_FLAG },
            { id: 'catalog-navigation', label: 'Navigation', route: '/catalog/navigation', icon: ListTree, flag: COLLECTIONS_FLAG },
            // { id: 'catalog-groups', label: 'Groups', route: '/catalog/groups', icon: Tag },
            // { id: 'catalog-gift-cards', label: 'Gift Cards', route: '/catalog/gift-cards', icon: Gift },
        ],
    },
    {
        id: 'procurement',
        label: 'Procurement',
        route: '/procurement/purchase-orders',
        icon: Truck,
        children: [
            { id: 'procurement-po', label: 'Purchase Orders', route: '/procurement/purchase-orders', icon: ClipboardList },
            { id: 'procurement-suppliers', label: 'Suppliers', route: '/procurement/suppliers', icon: Truck },
            // { id: 'procurement-transfers', label: 'Transfers', route: '/procurement/transfers', icon: ArrowLeftRight },
        ],
    },
    {
        id: 'inventory',
        label: 'Inventory',
        route: '/inventory',
        icon: Warehouse,
        children: [
            { id: 'inventory-overview', label: 'Stock Overview', route: '/inventory', icon: Warehouse },
            { id: 'inventory-adjustments', label: 'Adjustments', route: '/inventory/adjustments', icon: Wrench },
            { id: 'inventory-adjustment-reasons', label: 'Adjustment Reasons', route: '/inventory/adjustment-reasons', icon: BookOpen },
            // { id: 'inventory-stocktakes', label: 'Stocktakes', route: '/inventory/stocktakes', icon: ClipboardList },
        ],
    },
    {
        id: 'customers',
        label: 'Customers',
        route: '/customers',
        icon: Users,
        // children: [
        //     { id: 'customers-all', label: 'All Customers', route: '/customers', icon: Users },
        //     // { id: 'customers-segments', label: 'Segments', route: '/customers/segments', icon: UserCheck },
        // ],
    },
    {
        id: 'analytics',
        label: 'Analytics',
        route: '/analytics',
        icon: BarChart3,
        children: [
            { id: 'analytics-sales', label: 'Sales Reports', route: '/analytics/sales', icon: TrendingUp },
            // { id: 'analytics-inventory', label: 'Inventory Reports', route: '/analytics/inventory', icon: Activity },
            { id: 'analytics-profit', label: 'Profit Reports', route: '/analytics/profit', icon: DollarSign },
            // { id: 'analytics-customers', label: 'Customer Reports', route: '/analytics/customers', icon: Users },
        ],
    },
];

export const SETTINGS_NAV_ITEMS: NavItem[] = [
    {
        id: 'settings-general',
        label: 'General',
        route: '/settings/general',
        icon: Settings,
    },
    {
        id: 'settings-locations',
        label: 'Locations',
        route: '/settings/locations',
        icon: MapPin,
    },
    {
        id: 'settings-users-permissions',
        label: 'Users & Permissions',
        route: '/settings/users-permissions',
        icon: Users,
        children: [
            { id: 'settings-users', label: 'Users', route: '/settings/users-permissions/users', icon: Users },
            { id: 'settings-groups', label: 'Groups', route: '/settings/users-permissions/groups', icon: Users },
            { id: 'settings-roles', label: 'Roles', route: '/settings/users-permissions/roles', icon: Shield },
        ],
    },
    // Taxes & Duties is folded into the General settings tab (its Tax section, gated by
    // settings.taxes.*). Re-enable this entry if a standalone advanced-tax page is built.
    // {
    //     id: 'settings-taxes',
    //     label: 'Taxes & Duties',
    //     route: '/settings/taxes',
    //     icon: Receipt,
    // },
    // {
    //     id: 'settings-online-store',
    //     label: 'Online Store',
    //     route: '/settings/online-store',
    //     icon: Globe,
    //     children: [
    //         { id: 'settings-store-builder', label: 'Store Builder', route: '/settings/online-store/builder', icon: Store },
    //         { id: 'settings-search', label: 'Search Settings', route: '/settings/online-store/search', icon: Search },
    //         { id: 'settings-checkout', label: 'Cart & Checkout', route: '/settings/online-store/checkout', icon: ShoppingBag },
    //         { id: 'settings-markets', label: 'Markets', route: '/settings/online-store/markets', icon: Globe },
    //     ],
    // },
    // {
    //     id: 'settings-billing',
    //     label: 'Billing',
    //     route: '/settings/billing',
    //     icon: CreditCard,
    // },
    {
        id: 'settings-notifications',
        label: 'Notifications',
        route: '/settings/notifications',
        icon: Bell,
    },
];
